import { useQuery } from '@tanstack/react-query';

import { amendmentQueryKeys } from '@xemplo/gp-constants';
import {
  AmendmentExtended,
  AmendmentListItem,
  AmendmentTypes,
  ListPagedResult,
  PagedRequest,
  Result,
} from '@xemplo/gp-types';
import { getAssignedWorkerDisplayName, getRequestedByUserName } from '@xemplo/gp-utils';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { AmendmentAPIV2 } from './amendment-query.constants';
import { GetAmendmentsQueryStringV2, Id } from './amendment-query.types';

export const useGetAmendmentsQuery = (props: {
  requestParams?: PagedRequest<GetAmendmentsQueryStringV2>;
}) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams } = props;
  const url = apiUrlBuilder(
    AmendmentAPIV2.getAmendments,
    applicationApiUrl,
    requestParams,
  );

  return useQuery<ListPagedResult<AmendmentListItem>>({
    queryKey: amendmentQueryKeys.list(url),
    queryFn: ({ signal }) =>
      client
        .get<ListPagedResult<AmendmentListItem>>(url, { signal })
        .then((resp) => resp.data),
    enabled: !!applicationApiUrl,
  });
};

export const useGetAmendmentByIdQueryV2 = (props: { id?: Id }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { id } = props;
  const url = apiUrlBuilder(AmendmentAPIV2.getAmendmentById(id), applicationApiUrl);

  return useQuery({
    queryKey: amendmentQueryKeys.detail(String(id ?? ''), url),
    queryFn: ({ signal }) =>
      client.get<Result<AmendmentExtended>>(url, { signal }).then((resp) => resp.data),
    enabled: !!applicationApiUrl && !!id,
    select: mapAmendmentData,
  });
};

function mapAmendmentData(data: Result<AmendmentExtended>): AmendmentExtended {
  const {
    amendmentTypeId,
    lumpSumCalculationMethod,
    lumpSumNumberOfPayPeriods,
    recurringMaximumAmount,
    recurringExpiryDate,
    assignTo,
    requestedBy,
  } = data.result;

  const lumpSum = lumpSumCalculationMethod || lumpSumNumberOfPayPeriods ? 'Yes' : 'No';
  let recurringType: AmendmentExtended['recurringType'];
  if (recurringMaximumAmount != null) {
    recurringType = 1;
  } else if (recurringExpiryDate != null) {
    recurringType = 2;
  }

  return {
    ...data.result,
    lumpSum,
    recurringType,
    isRecurring: amendmentTypeId === AmendmentTypes.RECURRING,
    requestedByName: getRequestedByUserName(requestedBy),
    displayWorkerName: getAssignedWorkerDisplayName(assignTo),
  };
}
